import * as React from "react";
import { AlertDialog } from "@base-ui/react/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ConfirmDialogOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

interface ConfirmDialogContextValue {
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
}

const ConfirmDialogContext = React.createContext<ConfirmDialogContextValue | null>(null);

function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<ConfirmDialogOptions | null>(null);
  const resolverRef = React.useRef<((confirmed: boolean) => void) | null>(null);

  const closeWithResult = React.useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setOpen(false);
    setOptions(null);
  }, []);

  const confirm = React.useCallback((nextOptions: ConfirmDialogOptions) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setOptions(nextOptions);
      setOpen(true);
    });
  }, []);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && open) {
        closeWithResult(false);
      }
    },
    [closeWithResult, open],
  );

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}

      <AlertDialog.Root open={open} onOpenChange={handleOpenChange}>
        <AlertDialog.Portal>
          <AlertDialog.Backdrop className="fixed inset-0 z-[60] bg-white/70 backdrop-blur-sm data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 transition-opacity duration-150 ease-out" />
          <AlertDialog.Popup className="fixed left-1/2 top-1/2 z-[61] w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] sm:max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] data-[starting-style]:opacity-0 data-[starting-style]:scale-95 data-[ending-style]:opacity-0 data-[ending-style]:scale-95 transition-all duration-150 ease-out">
            <div className="flex flex-col gap-4 p-4 sm:p-6">
              <AlertDialog.Title className="text-xl font-bold font-display tracking-tight text-black">
                {options?.title}
              </AlertDialog.Title>
              {options?.description ? (
                <AlertDialog.Description className="text-sm text-[var(--ink-secondary)] leading-relaxed">
                  {options.description}
                </AlertDialog.Description>
              ) : null}
            </div>

            <div className="flex justify-end gap-3 px-4 py-3 sm:px-6 sm:py-4 border-t-2 border-black bg-[hsl(var(--muted))]">
              <Button type="button" variant="ghost" onClick={() => closeWithResult(false)}>
                {options?.cancelText ?? "Cancelar"}
              </Button>
              <Button
                type="button"
                variant={options?.destructive ? "destructive" : "accent"}
                className={cn(options?.destructive && "hover:text-[var(--error)]")}
                onClick={() => closeWithResult(true)}
              >
                {options?.confirmText ?? "Confirmar"}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmDialogContext.Provider>
  );
}

export { ConfirmDialogContext, ConfirmDialogProvider };
